import { Deserializer } from "jsonapi-serializer";
import { INTERNAL_SERVER_ERROR } from "../constants/response-status-codes";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { Zone } from "../models/Zone";
import { ZoneName } from "../models/ZoneName";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { ZoneWrapper } from "../models/wrappers/ZoneWrapper";
import { APIConnector } from "./api/APIConnector";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { Translator } from "../contexts/translator";
import { TranslationKeys } from "../contexts/TranslationKeys";

const apiConnector = new APIConnector();

export const getZoneNames = async (): Promise<ZoneName[]> => {
    try {
        const url = 'zone?fields[zone]=zoneName';
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, apiZones: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }
                const zones: ZoneName[] = apiZones.map(zone => {
                    return {
                        label: zone['zone-name'],
                        value: zone['id'],
                        isDefault: zone['is-default']
                    }
                });
                return resolve(zones);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getZoneNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        let filters = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filters = filters.replace('location.zoneByZoneId.zoneName', 'zoneName');
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const zoneFields = 'fields[zone]=zoneName,controller,isDefault';
        const controllerFields = 'fields[controller]=devName';        
        const url = `zone?${paginationString}&include=controller&${zoneFields}&${controllerFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, apiZones: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });;
                }
                const zones: Name[] = apiZones.map(zone => {
                    return {
                        label: zone['zone-name'],
                        value: zone['id'],
                        optionalColumns: [{ key: 'controller', value: zone["controller"]?.["dev-name"] }],
                        isDefault: zone['is-default']
                    }
                });
                return resolve({ rows: zones, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getZones = async (listParameters?: ListQuery): Promise<ZoneWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsZone = 'fields[zone]=zoneName,controller,zoneDefault,zoneNo,isDefault,totalDoors,zoneApb';
        const fieldsController = 'fields[controller]=devName,deviceImprox,site';
        const fieldsDeviceImprox = 'fields[deviceImprox]=devName';
        const fieldsSite = 'fields[site]=siteName';
        const url = `zone?include=controller,controller.deviceImprox,controller.deviceImprox.site${partialURL}&${fieldsZone}&${fieldsController}&${fieldsDeviceImprox}&${fieldsSite}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });;
                }

                const zones: Zone[] = data.map((zone): Zone => {

                    return {
                        id: zone["id"],
                        name: zone["zone-name"],
                        nameOriginal: zone["zone-name"],
                        site: zone["controller"]["site"]["site-name"],
                        siteId:zone["controller"]["site"]['id'],
                        antiPassback: zone["zone-apb"] === 0 ? "Off" : zone["zone-apb"] === 1 ? "Relaxed" : "Strict",
                        controllerId:zone["controller"]["id"],
                        controller: zone["controller"]["device-improx"]["dev-name"],
                        doors: zone["total-doors"],
                        default: zone["zone-default"],
                        zoneNo: zone["zone-no"],
                        isDefault: zone['is-default']
                    };
                });

                return resolve({ rows: zones, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getZoneById = async (id: string, translate: Translator): Promise<Zone> => {
    try {
        let includes = "include=controller,commonZone,controller.deviceImprox,";
        includes += "controller.deviceImprox.site,zoneParent";
        let zoneFields = "fields[zone]=zoneName,controller,zoneApb,totalDoors,";
        zoneFields += "zoneApblockoutdelay,zoneExitapblockoutdelay,zoneTaglimit,";
        zoneFields += "zoneRelaxedexit,zoneDefault,zoneParent,commonZone,zoneNo";
        const controllerFields = "fields[controller]=site,deviceImprox,devName";
        const siteFields = "fields[site]=siteName";
        const zoneParentFields = "fields[zoneParent]=zoneName";
        const commonZoneFields = "fields[commonZone]=czName";
        let url = `zone/${id}?${includes}&${zoneFields}&${controllerFields}&`;
        url += `${siteFields}&${zoneParentFields}&${commonZoneFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const zone: Zone = {
                    id: data["id"],
                    name: data["zone-name"],
                    site: data["controller"]["site"]["site-name"],
                    antiPassback: data["zone-apb"] === 0 ? translate.byKey(TranslationKeys.off) : data["zone-apb"] === 1 ? translate.byKey(TranslationKeys.relaxed) : translate.byKey(TranslationKeys.strict),
                    controller: data["controller"]["device-improx"]["dev-name"],
                    doors: data["total-doors"],
                    entryLockoutDelay: data["zone-apblockoutdelay"],
                    exitLockoutDelay: data["zone-exitapblockoutdelay"],
                    maxCapacity: data["zone-taglimit"],
                    relaxedTimeExit: data["zone-relaxedexit"],
                    controllerId: data["controller"]["device-improx"]["id"],
                    apbKey: (data["zone-apb"]).toString(),
                    siteId: data["controller"]["site"]["id"],
                    default: data["zone-default"],
                    nestedZoneId: data['zone-parent'] ? data['zone-parent']['id'] : undefined,
                    nestedZoneName: data['zone-parent'] ? data['zone-parent']['zone-name'] : undefined,
                    commonZoneId: data['common-zone'] ? data['common-zone']['id'] : undefined,
                    commonZoneName: data['common-zone'] ? data['common-zone']['cz-name'] : undefined,
                    zoneNo: data["zone-no"],
                };
                return resolve(zone);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const storeZone = async (zone: Zone, createZone = true): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: 'zone',
                id: zone.id,
                attributes: {
                    zoneApb: zone.apbKey,
                    zoneApblockoutdelay: zone.entryLockoutDelay,
                    zoneDefault: zone.default ?? 0,
                    zoneExitapblockoutdelay: zone.exitLockoutDelay,
                    zoneName: zone.name,
                    zoneNo: zone.zoneNo,
                    zoneRelaxedexit: zone.relaxedTimeExit === false ? 0 : 1,
                    zoneTaglimit: zone.maxCapacity,
                    ...getExtraAttributes()
                },
                relationships: {
                    ...getSaveZoneRelationships(zone.controllerId, zone.commonZoneId, zone.nestedZoneId)
                }
            }
        };

        const url = `zone${createZone ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createZone, newRecord)
            .then(data => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createZone) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: getConstraintViolation(content.errors[0].detail), success: false })
                    }

                    new Deserializer().deserialize(content, (error, newZone) => {
                        if (error) {
                            return reject({status: response.status, id: zone.id, success: false });
                        }
                        return resolve({ id: newZone.id, success: true });
                    })

                    return;
                }

                if (response.status === 204) {
                    return resolve({ id: zone.id, success: true })
                }

                const content = await response.json();
                return reject({status: response.status, id: zone.id, success: false, message: getConstraintViolation(content.errors[0].detail) });
            }

            return reject({status: INTERNAL_SERVER_ERROR, id: zone.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteZone = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `zone/${id}`;
        const zone = {
            data: {
                type: "zone",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, zone)

        return new Promise(async (resolve, reject) => {
            if (response.status === 204) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getZoneNamesWithPaginationExcluding = async (zoneId: string, listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        let filters = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filters = filters.replace('location.zoneByZoneId.zoneName', 'zoneName');
        filters = (filters === '' ? `&filter=id!=${zoneId}` : `${filters};id!=${zoneId}`);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const url = `zone?${paginationString}&include=controller.deviceImprox`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, apiZones: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }
                const zones: Name[] = apiZones.map(zone => { return { label: zone['zone-name'], value: zone['id'], optionalColumns: [{ key: 'controller', value: zone["controller"]?.["device-improx"]?.["dev-name"] }] } });
                return resolve({ rows: zones, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getZonesForAController = async (controllerId: string, listParameters?: ListQuery): Promise<ZoneWrapper> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = (filter === '' ? `&filter=controller.id==${controllerId}` : `${filter};controller.id==${controllerId}`);

        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const zoneFields = "fields[zone]=zoneName,controller,zoneDefault,zoneNo,isDefault,areaLocationCounts,totalDoors,zoneApb";
        const controllerFields = "fields[controller]=deviceImprox,site";
        const deviceImproxFields = "fields[deviceImprox]=devName"
        const siteFields = "fields['site']=siteName"
        const includes = 'include=controller.deviceImprox,commonZone,controller.site';
        const url = `zone?${includes}&${zoneFields}&${controllerFields}&${deviceImproxFields}&${siteFields}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const zones: Zone[] = data.map((zone): Zone => {
                    return {
                        id: zone["id"],
                        name: zone["zone-name"],
                        site: zone["controller"]["site"]["site-name"],
                        siteId: zone["controller"]["site"]['id'],
                        antiPassback: zone["zone-apb"] === 0 ? "Off" : zone["zone-apb"] === 1 ? "Relaxed" : "Strict",
                        controllerId: zone["controller"]["id"],
                        controller: zone["controller"]["device-improx"]["dev-name"],
                        doors: zone["total-doors"],
                        default: zone["zone-default"],
                        zoneNo: zone["zone-no"],
                        isDefault: zone['is-default'],
                        areaLocationCounts:zone['area-location-counts']?.length > 0? zone['area-location-counts'] : []
                    };
                });
                return resolve({ rows: zones, pagination });
            });
        });

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const getSaveZoneRelationships = (controllerId?: number, commonZoneId?: number, nestedZoneId?: number) => {
    if (commonZoneId && nestedZoneId) {
        return {
            controller: {
                data: {
                    type: 'controller',
                    id: controllerId
                }
            },
            commonZone: {
                data: {
                    type: "commonZone",
                    id: commonZoneId
                }
            },
            zoneParent: {
                data: {
                    type: "zone",
                    id: nestedZoneId
                }
            }
        }
    } else if (commonZoneId) {
        return {
            controller: {
                data: {
                    type: 'controller',
                    id: controllerId
                }
            },
            commonZone: {
                data: {
                    type: "commonZone",
                    id: commonZoneId
                }
            }
        }
    } else if (nestedZoneId) {
        return {
            controller: {
                data: {
                    type: 'controller',
                    id: controllerId
                }
            },
            zoneParent: {
                data: {
                    type: "zone",
                    id: nestedZoneId
                }
            }
        }
    }

    return {
        controller: {
            data: {
                type: 'controller',
                id: controllerId
            }
        }
    }
}


/**
 * This is mainly for removing nested zone, common zone or both. We are using this because relationships doesn't accept nulls.
 * For saving we push information using relationships but for removing we use attributes
 */

const getExtraAttributes = (commonZoneId?: number, nestedZoneId?: number) => {
    if (!commonZoneId && !nestedZoneId) {
        return {
            commonZone: null,
            zoneParent: null
        }
    } else if (!commonZoneId) {
        return {
            commonZoneId: null
        }
    } else if (!nestedZoneId) {
        return {
            nestedZoneId: null
        }
    }

    return {}
}
